import { useEffect, useState } from 'react';
import { apiClient } from '@/lib/apiClient';
import { Card } from '@/components/ui/Card';

interface ChannelMetrics {
  channel: string;
  leadCount: number;
  qualifiedCount: number;
  convertedCount: number;
  wonCount: number;
  qualificationRate: number;
  conversionRate: number;
  winRate: number;
}

interface ChannelCampaignMetrics {
  channel: string;
  campaignId: string | null;
  campaignName: string | null;
  leadCount: number;
  qualifiedCount: number;
  convertedCount: number;
  qualificationRate: number;
  conversionRate: number;
}

const CHANNEL_LABELS: Record<string, string> = {
  INSTAGRAM: 'Instagram',
  FACEBOOK: 'Facebook',
  LINKEDIN: 'LinkedIn',
  X: 'X (Twitter)',
  TIKTOK: 'TikTok',
  WEB_FORM: 'Web form',
  MANUAL: 'Manual',
  CSV_IMPORT: 'CSV import',
  REFERRAL: 'Referral',
  OTHER: 'Other',
  UNATTRIBUTED: 'Unattributed',
};

/**
 * Story 48.7 — channel attribution dashboard.
 *
 * Two views on one page:
 *   - Top section: per-channel totals + horizontal bars + headline rates.
 *   - Bottom section: per-campaign breakdown table.
 *
 * No charting library — bars are CSS divs scaled against the max count
 * in the dataset. Sufficient for v1; recharts can land later if a
 * stakeholder asks for richer visualizations.
 */
export function ChannelAnalyticsPage() {
  const [channels, setChannels] = useState<ChannelMetrics[]>([]);
  const [byCampaign, setByCampaign] = useState<ChannelCampaignMetrics[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      apiClient.get<ChannelMetrics[]>('/analytics/channels'),
      apiClient.get<ChannelCampaignMetrics[]>('/analytics/channels/by-campaign'),
    ])
      .then(([c, cc]) => {
        if (cancelled) return;
        setChannels(c.data);
        setByCampaign(cc.data);
      })
      .catch(() => {
        if (cancelled) return;
        setChannels([]);
        setByCampaign([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const maxLeads = Math.max(1, ...channels.map((c) => c.leadCount));
  const totalLeads = channels.reduce((sum, c) => sum + c.leadCount, 0);

  if (loading) {
    return <p className="p-6 text-sm text-muted-foreground">Loading…</p>;
  }

  if (channels.length === 0) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-semibold">Channel attribution</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          No leads with attribution data yet. Once leads start arriving from
          social-network ads or hosted forms, this view will show per-channel
          performance.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <header>
        <h1 className="text-2xl font-semibold">Channel attribution</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {totalLeads.toLocaleString()} leads across {channels.length} channels.
        </p>
      </header>

      <Card>
        <h2 className="mb-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Lead volume by channel
        </h2>
        <div className="space-y-3">
          {channels.map((c) => (
            <div key={c.channel} className="space-y-1">
              <div className="flex items-baseline justify-between text-sm">
                <span className="font-medium">{CHANNEL_LABELS[c.channel] ?? c.channel}</span>
                <span className="font-mono text-xs text-muted-foreground">
                  {c.leadCount.toLocaleString()} leads · {(c.qualificationRate * 100).toFixed(1)}% qualified
                </span>
              </div>
              <div className="h-2 w-full rounded-full bg-muted">
                <div
                  className="h-2 rounded-full bg-primary"
                  style={{
                    width: `${(c.leadCount / maxLeads) * 100}%`,
                    minWidth: c.leadCount > 0 ? '0.25rem' : 0,
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Card className="overflow-hidden p-0">
        <div className="border-b bg-muted/40 px-4 py-3">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Funnel by channel
          </h2>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-muted text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-4 py-2 text-left">Channel</th>
              <th className="px-4 py-2 text-right">Leads</th>
              <th className="px-4 py-2 text-right">Qualified</th>
              <th className="px-4 py-2 text-right">Converted</th>
              <th className="px-4 py-2 text-right">Won</th>
              <th className="px-4 py-2 text-right">Qual rate</th>
              <th className="px-4 py-2 text-right">Conv rate</th>
              <th className="px-4 py-2 text-right">Win rate</th>
            </tr>
          </thead>
          <tbody>
            {channels.map((c) => (
              <tr key={c.channel} className="border-t">
                <td className="px-4 py-2 font-medium">
                  {CHANNEL_LABELS[c.channel] ?? c.channel}
                </td>
                <td className="px-4 py-2 text-right font-mono">{c.leadCount}</td>
                <td className="px-4 py-2 text-right font-mono">{c.qualifiedCount}</td>
                <td className="px-4 py-2 text-right font-mono">{c.convertedCount}</td>
                <td className="px-4 py-2 text-right font-mono">{c.wonCount}</td>
                <td className="px-4 py-2 text-right font-mono">
                  {(c.qualificationRate * 100).toFixed(1)}%
                </td>
                <td className="px-4 py-2 text-right font-mono">
                  {(c.conversionRate * 100).toFixed(1)}%
                </td>
                <td className="px-4 py-2 text-right font-mono">
                  {(c.winRate * 100).toFixed(1)}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {byCampaign.length > 0 && (
        <Card className="overflow-hidden p-0">
          <div className="border-b bg-muted/40 px-4 py-3">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Channel × campaign breakdown
            </h2>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-muted text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-2 text-left">Campaign</th>
                <th className="px-4 py-2 text-left">Channel</th>
                <th className="px-4 py-2 text-right">Leads</th>
                <th className="px-4 py-2 text-right">Qualified</th>
                <th className="px-4 py-2 text-right">Converted</th>
                <th className="px-4 py-2 text-right">Qual rate</th>
                <th className="px-4 py-2 text-right">Conv rate</th>
              </tr>
            </thead>
            <tbody>
              {byCampaign.map((row, i) => (
                <tr key={`${row.campaignId}-${row.channel}-${i}`} className="border-t">
                  <td className="px-4 py-2">{row.campaignName ?? '—'}</td>
                  <td className="px-4 py-2 text-muted-foreground">
                    {CHANNEL_LABELS[row.channel] ?? row.channel}
                  </td>
                  <td className="px-4 py-2 text-right font-mono">{row.leadCount}</td>
                  <td className="px-4 py-2 text-right font-mono">{row.qualifiedCount}</td>
                  <td className="px-4 py-2 text-right font-mono">{row.convertedCount}</td>
                  <td className="px-4 py-2 text-right font-mono">
                    {(row.qualificationRate * 100).toFixed(1)}%
                  </td>
                  <td className="px-4 py-2 text-right font-mono">
                    {(row.conversionRate * 100).toFixed(1)}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
